// Loads a resort package (resorts/<id>/) and applies its brand to the page.
//   resort.json      spatial + visual config (models, props, pins, paddocks, UI toggles, brand)
//   content.json     guest-facing text
//   assignments.json survey footprint id (MML mtk_id) -> place key
//   scene.json       generated geometry (terrain, footprints, roads, water, trees)
// An unsaved editor draft (localStorage) is layered on top, so corrections show immediately.
import { initContent, setContentLang } from './content.js';
import { extendRegistry, resolveAssets } from './settings/index.js'; // WS-49: the one settings list; WS-50: asset keys as addresses
import { createRuntime } from './runtime.js'; // WS-49: holds the layers, live apply
import { legacyPreset } from './legacy.js'; // WS-49: the implicit preset of today's page
import { allowedOrigins, takeReload, listenEarly } from './bridge.js'; // WS-51: what the editor's `reload` kept; the editor's first message

const params = new URLSearchParams(location.search);
/** WS-75: `?renderer=webgpu-fallback` = WebGPURenderer forced onto its WebGL2 backend (a QA flag: how older iPhones see the map; not a setting). */
export const FORCE_WEBGL = params.get('renderer') === 'webgpu-fallback';
// WS-23 runtime (begin): the public host (worker/index.js) pins the resort and the environment with
// <meta name="mapmystay-resort"> / <meta name="mapmystay-env">. A pinned resort ignores ?resort (one host
// = one tenant). Without the metas (local dev server) everything stays as before: ?resort, drafts, ?edit.
const meta = (n) => document.querySelector(`meta[name="${n}"]`)?.content || '';
export const RUNTIME = Object.freeze({
  env: ['production', 'preview'].includes(meta('mapmystay-env')) ? meta('mapmystay-env') : 'local',
  get allowDrafts() { return this.env !== 'production'; }, // ?preview, ?edit and editor drafts: never on production hosts
});
const PINNED = meta('mapmystay-resort').replace(/[^a-z0-9-]/g, '');
export const RESORT_ID = PINNED || (params.get('resort') || 'aurora-village').replace(/[^a-z0-9-]/g, '');
// WS-23 runtime (end)
// ---- WS-49 / WS-50 boot record (begin): what the host pins on the page, one <meta name="mapmystay-<field>"> per
// field (worker/store.js BOOT_META, contract section 10). The engine knows nothing else about its host: the asset
// root, the bundle and the beacon are addresses the record names, each kept only on this page's own origin. A field
// that is absent leaves today's behaviour. A pinned preset is honoured on every host, `?preset=<slug>` on draft hosts
// only. `editor` lists the origins that may frame the page and drive it (bridge.js).
const idOnly = (s) => (s || '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40);
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
/**
 * @param {(field: string) => string} meta  the content of `mapmystay-<field>`, '' when absent
 * @param {URLSearchParams} params
 * @param {{ drafts: boolean, origin: string, base: string }} page  draft host?, the page's origin, its base address
 */
export function readBoot(meta, params, { drafts, origin, base }) {
  const own = (addr) => { // an address on this page's own origin; anything else is not used
    try { const u = addr.trim() ? new URL(addr, base) : null; return u?.origin === origin ? u : null; } catch { return null; } // errors:ignore: not an address
  };
  const path = (u) => (u ? u.pathname + u.search : '');
  const values = (() => { try { return JSON.parse(meta('placement-values') || 'null'); } catch { return null; } })(); // errors:ignore: not JSON
  const flags = (() => { try { return JSON.parse(meta('flags') || 'null'); } catch { return null; } })(); // errors:ignore: not JSON
  const placement = idOnly(meta('placement'));
  const here = meta('here').replace(/[^\w-]/g, '').slice(0, 64);
  // WS-91 part 2: the sign's own words for its marker (QrRecord.label), JSON text of a Translatable, at most 400 characters
  const label = (() => { try { const l = meta('here-label'); return l.length <= 400 ? JSON.parse(l || 'null') : null; } catch { return null; } })(); // errors:ignore: not JSON
  const words = (t) => (typeof t === 'string' && t.trim() && [...t.trim()].length <= 60 ? t.trim() : null);
  const colors = (() => { try { return JSON.parse(meta('colors') || 'null'); } catch { return null; } })(); // errors:ignore: not JSON
  const hex = (v) => typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v);
  const texts = isObj(label) ? Object.entries(label).filter(([k, v]) => /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(k) && words(v)).map(([k, v]) => [k, words(v)]) : [];
  return {
    map: idOnly(meta('map')),
    preset: idOnly(meta('preset')) || (drafts ? idOnly(params.get('preset')) : ''),
    placement,
    values: placement && isObj(values) ? values : null, // the placement's own values (layer 6)
    bundle: path(own(meta('bundle'))),
    assets: own(meta('assets'))?.pathname.replace(/\/?$/, '/') || '', // '' = relative addresses, as today
    objects: own(meta('objects'))?.pathname.replace(/\/?$/, '/') || '', // where the bundle's asset keys are served; '' = this host serves none
    beacon: path(own(meta('beacon'))),
    version: meta('version').replace(/\D/g, '').slice(0, 12),
    editors: meta('editor').split(/\s+/).filter(Boolean),
    // WS-51 (interface commit): the entitlement switches as they are NOW (worker/store.js FlagsRecord), true or false
    // only; null = none pinned, the bundle's own `flags` stand. Read here; track T3 lays them over `bundle.flags`.
    flags: isObj(flags) ? Object.fromEntries(Object.entries(flags).filter(([, on]) => typeof on === 'boolean')) : null,
    // WS-52: the place of a wayfinding QR code ("you are here"), pinned by the host from the code's record: never an
    // address parameter, so a visitor cannot move the marker. '' = no marker (and lazy/marks.js is not fetched).
    here,
    // WS-91: where exactly that marker stands, `x,z` in the map's metres (the sign's fine-tuned spot, QrRecord.spot);
    // null = the middle of the place. Only with `here`, and as `here` from the record alone
    hereSpot: ((s) => (s.length === 2 && s.every((v) => Number.isFinite(v) && Math.abs(v) <= 20000) ? s : null))(meta('here-spot').split(',').filter(Boolean).map(Number)),
    // WS-91 part 2: the words the marker carries once the guest's own dot shows (a text, or one per language, each 1 to
    // 60 characters); null = the place's own name. Only with `here`, and as `here` from the record alone
    hereLabel: !here ? null : typeof label === 'string' ? words(label) : texts.length ? Object.fromEntries(texts) : null,
    // WS-60: the client's map style (js/lazy/styles/); `?style=<id>` on draft hosts previews another one
    style: idOnly((drafts && params.get('style')) || meta('style')),
    // WS-73: the websites whose cookie banner may allow click statistics (https origins only); [] = clicks never leave
    consent: meta('consent').split(/\s+/).filter((o) => /^https:\/\/(\*\.)?([a-z0-9-]+\.)+[a-z]{2,}(:\d{2,5})?$/.test(o)),
    // VIS-208: the client's own button and panel colours for all its maps (worker/store.js BrandColors), over the
    // package's brand colours; null = the colours the studio set
    colors: isObj(colors) && hex(colors.accent) && hex(colors.ink) ? { accent: colors.accent, ink: colors.ink } : null,
  };
}
export const BOOT = Object.freeze(readBoot((f) => meta(`mapmystay-${f}`), params, { drafts: RUNTIME.allowDrafts, origin: location.origin, base: document.baseURI }));
/** Today's `?embed` / `?kiosk`: they pick the implicit legacy preset, so they mean nothing under a real one. */
export const MODES = BOOT.preset ? [] : ['embed', 'kiosk'].filter((m) => params.has(m));
/** A QA parameter (`debug`, `tm`, `ao`, `cam`, ...): under a preset it works on draft hosts only. */
export const qa = (name) => (BOOT.preset && !RUNTIME.allowDrafts ? null : params.get(name));
const storage = (() => { try { return localStorage; } catch { return null; } })(); // errors:ignore: storage blocked
/**
 * The bundle and the preset of a page (contract sections 3 and 8). The compiled bundle the boot record names is read
 * on every host: a published map is how a preset goes live. Without that address the package's seed `bundle.json` is
 * read on draft hosts only; a public host then asks for nothing. Whatever goes wrong, the answer is "no preset" with
 * the reason in `problem`, and the page shows the package's default view.
 *
 * The package's additions to the settings list (`settings`, for `extendRegistry`):
 *   - a COMPILED bundle carries them itself, inside `bundle.settings` (the compile of delivery 2), so a published page
 *     asks for ONE file. It never requests the package's `settings.json`: production hosts do not have that file;
 *   - the hand-written SEED is read beside its `settings.json` (draft hosts only).
 * The bundle's asset references (gallery items `{ asset }`) become addresses under `boot.objects`, the place this
 * host serves the bucket keys of `bundle.assets`. A host that names no such place serves none: those items are left
 * out rather than shown as broken images.
 * @param {{ preset: string, bundle: string, objects?: string }} boot
 * @param {{ base: string, drafts: boolean, get: (url: string) => Promise<any> }} io  `get` resolves to JSON or null
 * @returns {Promise<{ bundle: object | null, settings: object | null, preset: object | null, problem: '' | 'no-bundle' | 'bundle-unavailable' | 'unknown-preset' }>}
 */
export async function bootBundle(boot, { base, drafts, get }) {
  const none = (problem) => ({ bundle: null, settings: null, preset: null, problem });
  if (!boot.preset) return none(''); // today's page: no new file is requested
  const compiled = !!boot.bundle;
  const src = boot.bundle || (drafts ? `${base}bundle.json` : '');
  if (!src) return none('no-bundle');
  const [raw, file] = await Promise.all([get(src), compiled ? null : get(`${base}settings.json`)]);
  if (!isObj(raw)) return none('bundle-unavailable');
  const preset = (Array.isArray(raw.presets) ? raw.presets : []).find((p) => isObj(p) && p.enabled !== false && (p.slug === boot.preset || p.id === boot.preset));
  if (!preset) return none('unknown-preset');
  const carried = isObj(raw.settings) && raw.settings.schema === 1 ? raw.settings : null; // the seven package fields of a compiled bundle
  const bundle = resolveAssets(boot.objects ? raw : { ...raw, assets: {} }, boot.objects || '');
  return { bundle, settings: compiled ? carried : isObj(file) ? file : null, preset, problem: '' };
}
// ---- WS-49 / WS-50 boot record (end)
// ---- WS-51 (begin): the flags record, what the editor's `reload` kept, and the places a preset hides. Pure functions
// (tests/unit/runtime/editor.test.mjs); loadResort below applies them. None of them acts on today's page: no preset,
// no editor origin and no hidden place means every one of them hands back what it was given.
/**
 * The entitlement switches as they are NOW (the boot field `flags`, worker/store.js FlagsRecord) laid over the
 * compiled bundle's own `flags`. Only switches a gate of the settings list reads are taken; a switch the record
 * does not name keeps the bundle's value. No record = the bundle as it is; no bundle = nothing (today's page).
 * A bundle without `flags` gates nothing (as before), so there the switches the record leaves out stay on.
 * @param {object | null} bundle  @param {{ [gate: string]: boolean } | null} pinned  @param {import('./settings/typedefs.js').Registry} registry
 */
export function layFlags(bundle, pinned, registry) {
  if (!isObj(bundle) || !isObj(pinned)) return bundle;
  const gates = new Set(registry.settings.flatMap((e) => [e.gate, ...Object.values(e.gateValues || {})]).filter(Boolean));
  const now = Object.entries(pinned).filter(([gate, on]) => gates.has(gate) && typeof on === 'boolean');
  if (!now.length) return bundle;
  const own = isObj(bundle.flags) ? bundle.flags : Object.fromEntries([...gates].map((gate) => [gate, true]));
  return { ...bundle, flags: { ...own, ...Object.fromEntries(now) } };
}
/**
 * WS-55: this page is (very likely) the editor's frame: an origin of the boot record's `editor` list may frame it (the
 * app host writes that list, never the public host) and it is framed. There the scene is built with every place,
 * hidden ones too, so a place hidden or shown again in the editor changes on the map at once (main.js "WS-55").
 * @param {{ editors: string[] }} boot  @param {{ parent?: any }} win
 */
export function editorFramed(boot, win) {
  try { return !!allowedOrigins(boot.editors).size && !!win.parent && win.parent !== win; } catch { return false; } // errors:ignore: a foreign parent
}
/**
 * What the editor's `reload` kept for this page (bridge.js), or null. Read once and deleted. Only a page that an
 * origin of the boot record's `editor` list may frame, and that IS framed, looks at all: a public page never does.
 * @param {{ map: string, preset: string, editors: string[] }} boot
 * @param {{ parent?: any, sessionStorage?: Storage }} win
 */
export function readRestore(boot, win) {
  try {
    if (!boot.preset || !editorFramed(boot, win)) return null;
    return takeReload(win.sessionStorage, { map: boot.map, preset: boot.preset });
  } catch { return null; } // errors:ignore: storage blocked
}
/**
 * What a preset hides (`places.hidden`: place, unit and unit type keys; `categories.hidden`: category ids of the
 * content), as a test of one key. null when nothing is hidden, so the page takes its usual path untouched.
 * A unit is hidden with its type, and every unit with the category of the unit types (`stay`, content.js).
 * @param {string[]} places  @param {string[]} categories
 * @param {object} resort  resort.json (`units`: which keys are units)
 * @param {object[]} contents  the content of every season the page can show (a place's category is read there)
 */
export function hiddenPlaces(places, categories, resort, contents = []) {
  const list = (a) => (Array.isArray(a) ? a.filter((s) => typeof s === 'string' && s) : []);
  const keys = new Set(list(places)), cats = new Set(list(categories));
  if (!keys.size && !cats.size) return null;
  const units = Object.entries(isObj(resort?.units) ? resort.units : {}).filter(([k]) => !k.startsWith('_'));
  const typeOf = (key) => { // content.js unitOf: the type whose pattern (and numbers) the key fits
    let match = null;
    for (const [type, u] of units) {
      const m = key.match(new RegExp(u.pattern));
      if (!m) continue;
      if (u.numbers) { if (u.numbers.includes(Number(m[1]))) return type; continue; }
      match ||= type;
    }
    return match;
  };
  /** @param {string} key  @param {object | object[]} [docs] the content to read the key's category from */
  const has = (key, docs = contents) => {
    if (typeof key !== 'string' || !key) return false;
    if (keys.has(key)) return true;
    const all = [docs].flat().filter(isObj), isType = (t) => !!t && all.some((d) => isObj(d.unitTypes?.[t]));
    const type = isType(key) ? key : typeOf(key);
    if (isType(type)) return keys.has(type) || cats.has('stay');
    return cats.has(all.find((d) => isObj(d.places?.[key]))?.places[key].category);
  };
  return { has, keys, categories: cats };
}
/**
 * 2026-10-20: a surveyed building the preset hides (main.js does not build it). A named building is hidden by its
 * place key; an unnamed one by its footprint id `mtk-<id>`, the key the editor writes for it. On every page: the
 * key a building's press answers to (main.js anonKey) is a different question, and outside the editor's frame an
 * unnamed building without texts has none, so it must not decide whether the building is built.
 * @param {{ has(key: string): boolean } | null} hidden  hiddenPlaces()  @param {{ key?: string | null, mtk?: number | string | null }} b
 */
export function buildingHidden(hidden, b) {
  return !!hidden && (hidden.has(b.key) || (b.mtk != null && hidden.has(`mtk-${b.mtk}`)));
}
/**
 * A copy of a content document (content.json, or an overlay laid over it) without what the preset hides: its
 * places, unit types, list entries and category tabs. `base`: the content the keys' categories are read from (an
 * overlay seldom repeats them). Nothing hidden = the document itself.
 */
// ---- card photos by season (begin, 2026-10-15): a content overlay (the bundle's) may carry its own
// `seasonal.<season>` patch, as content.json does. The overlay is laid over the season's content, so without this a
// gallery or a fact list in the bundle would hide the package's summer and autumn versions of it.
function overlaySeason(doc, season) {
  if (!isObj(doc) || !isObj(doc.seasonal)) return doc;
  const { seasonal, ...rest } = doc;
  const out = structuredClone(rest);
  if (isObj(seasonal[season])) seasonMerge(out, structuredClone(seasonal[season]));
  return out;
}
// ---- card photos by season (end)
export function withoutHidden(doc, hidden, base = doc) {
  if (!hidden || !isObj(doc)) return doc;
  const keep = (group) => Object.fromEntries(Object.entries(group).filter(([k]) => !hidden.has(k, [base, doc])));
  return {
    ...doc,
    ...(isObj(doc.places) && { places: keep(doc.places) }),
    ...(isObj(doc.unitTypes) && { unitTypes: keep(doc.unitTypes) }),
    ...(Array.isArray(doc.listed) && { listed: doc.listed.filter((k) => !hidden.has(k, [base, doc])) }),
    ...(Array.isArray(doc.categories) && { categories: doc.categories.filter((c) => !hidden.categories.has(c?.id)) }),
  };
}
/**
 * The editor's unsaved texts or photos (layer 9) as a content overlay. A gallery item of the `{ asset }` form gets
 * its address under the objects root of this page, as the bundle's items do (no root = not shown); the item's own
 * `src`, a photo uploaded a moment ago, is used only when it is an address on this page's own origin.
 */
export function editorContent(doc, bundle, objects, origin, base) {
  if (!isObj(doc)) return null;
  const own = (g) => { try { return new URL(g.src, base).origin === origin; } catch { return false; } }; // errors:ignore: not an address
  const item = (g) => { if (!isObj(g) || typeof g.asset !== 'string' || typeof g.src !== 'string' || own(g)) return g; const rest = { ...g }; delete rest.src; return rest; };
  const group = (entries) => (isObj(entries) ? Object.fromEntries(Object.entries(entries).map(([k, e]) => [k, isObj(e) && Array.isArray(e.gallery) ? { ...e, gallery: e.gallery.map(item) } : e])) : entries);
  const content = { ...doc };
  for (const k of ['unitTypes', 'places']) if (k in content) content[k] = group(content[k]);
  return resolveAssets({ content, assets: objects && isObj(bundle?.assets) ? bundle.assets : {} }, objects || '').content;
}
export const RESTORED = readRestore(BOOT, globalThis);
/** VIS-267: a visitor's page lets go of the CPU copies of what the graphics chip holds (gpu/release.js). `?edit` and the
 *  editor's frame (Fine-tune, Film, Renders, live touches) keep every array: main.js EDIT / LIVE_HIDE, the same tests. */
export const releaseCpuOn = (edit, framed) => !edit && !framed;
export const RELEASE_CPU = releaseCpuOn(params.has('edit') && RUNTIME.allowDrafts, editorFramed(BOOT, globalThis));
listenEarly(BOOT.editors, globalThis); // the editor frame's first message arrives before the map is built: keep it (inert on a page no editor origin frames)
// ---- WS-51 (end)
export const BASE =`${BOOT.assets}resorts/${RESORT_ID}/`; // under the asset root the host names; relative without one
const DRAFT_KEY = `resort-map-draft:${RESORT_ID}`;

export function loadDraft() {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); } catch { return null; }
}
export function saveDraft(draft) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); } catch { /* storage unavailable */ }
}
export function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch { /* storage unavailable */ }
}

export async function loadResort(onProgress = () => {}) {
  const get = (f) => fetch(BASE + f).then((r) => { if (!r.ok) throw new Error(`${f}: ${r.status}`); return r.json(); });
  const opt = (url) => fetch(url).then((r) => (r.ok ? r.json() : null)).catch(() => null);
  // VIS-239: a published map's bundle (its address is in the boot record) downloads with the package files, not after them
  const bundleP = BOOT.preset && BOOT.bundle ? opt(BOOT.bundle) : null;
  // WS-72 step 6: the survey (the biggest file) downloads alongside the package files instead of after them
  const sceneP = get('scene.json');
  sceneP?.catch(() => {}); // errors:ignore: awaited (and so thrown) below; this only stops an early failure counting as unhandled
  const [resort, content, assignments] = await Promise.all([get('resort.json'), get('content.json'), get('assignments.json')]);
  onProgress(0.1, 'Reading the survey');
  const scene = await sceneP;
  // Preview gate (orchestrator, round 4): ?preview=a,b layers resorts/<id>/previews/<topic>.json
  // ({ resort?, assign? }, same shape as the editor draft) over the live package, in the order given.
  // Drafts stay off the default view until the user approves; going live = merging the patch into
  // resort.json / assignments.json and deleting the preview file.
  const previews = (RUNTIME.allowDrafts ? params.get('preview') || '' : '').split(',').map((s) => s.trim().replace(/[^a-z0-9-]/g, '')).filter(Boolean);
  for (const topic of previews) {
    const p = await fetch(`${BASE}previews/${topic}.json`, { cache: 'no-store' }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    if (!p) { console.warn(`preview "${topic}": no previews/${topic}.json`); continue; }
    for (const [mtk, v] of Object.entries(p.assign || {})) { if (v === null) delete assignments.assign[mtk]; else assignments.assign[mtk] = v; }
    if (p.resort) deepMerge(resort, p.resort);
    if (p.content) deepMerge(content, p.content); // WS-12: previews may also add or split cards (content.json)
  }
  resort.previews = previews; // engine code may check resort.previews.includes('<topic>') only for preview-only UI
  const draft = params.has('nodraft') || !RUNTIME.allowDrafts ? null : loadDraft();
  if (draft) {
    if (draft.assign) Object.assign(assignments.assign, draft.assign);
    for (const [mtk, v] of Object.entries(draft.assign || {})) if (v === null) delete assignments.assign[mtk];
    // VIS-196: the ?edit draft holds assignments only; positions are Fine-tune changes. An older draft's moved props,
    // pins, paddocks and model parameters are dropped once, so localhost shows the same map as the live one.
    if (draft.resort) { console.warn('[draft] dropped old ?edit positions and parameters (move things in Fine-tune):', Object.keys(draft.resort).join(', ')); saveDraft({ assign: draft.assign || {} }); }
  }
  let unpatched = params.has('edit') ? null : { resort: structuredClone(resort), content: structuredClone(content) }; // WS-41
  // ---- WS-49 platform boot (begin): one code path. A preset comes from the bundle (bootBundle above; + the package's
  // settings.json, the resort-specific settings); with none, legacy.js builds the implicit preset of today's page and
  // no new file is requested. The runtime holds the layers; everything below reads the resolved values.
  const booted = await bootBundle(BOOT, { base: BASE, drafts: RUNTIME.allowDrafts, get: (url) => (bundleP && url === BOOT.bundle ? bundleP : opt(url)) });
  const { settings, preset, problem } = booted;
  if (problem) console.warn(`preset "${BOOT.preset}": ${problem}; showing the default view`);
  // a published map whose bundle did not arrive is a failure worth a report (errors.js, already on the page)
  if (problem === 'bundle-unavailable' && BOOT.bundle) import('./errors.js').then((m) => m.reportError(new Error('bundle unavailable'), { area: 'boot', map: BOOT.map, version: BOOT.version })).catch(() => {}); // errors:ignore: reporting is best effort
  const mapId = BOOT.map || booted.bundle?.map || RESORT_ID;
  // the ids every statistics event carries (track.js reads these metas); the host pins them, a draft host's ?preset has none
  for (const [name, v] of preset ? [['map', mapId], ['preset', preset.id]] : []) {
    const m = document.querySelector(`meta[name="mapmystay-${name}"]`) || document.head.appendChild(Object.assign(document.createElement('meta'), { name: `mapmystay-${name}` }));
    m.content = v;
  }
  const registry = extendRegistry(settings);
  for (const p of registry.problems) console.warn(`settings.json ${p.path}: ${p.message}`);
  // WS-51: the entitlement switches as they are now (the boot field `flags`) over the bundle's own
  const bundle = layFlags(booted.bundle, BOOT.flags, registry);
  // WS-51: a page that comes back from the editor's `reload` starts with what it kept: the editor's unsaved values and
  // its copy of the preset, and what the visitor had pressed (time, season, selection). Only under a real preset.
  const back = preset ? RESTORED : null, kept = back?.layers.preset;
  const rt = createRuntime({
    registry, storage,
    layers: {
      resort: unpatched?.resort || resort, content: unpatched?.content || content, bundle,
      preset: (kept?.id === preset?.id && kept) || preset || legacyPreset(unpatched?.resort || resort, MODES),
      placement: BOOT.placement ? { id: BOOT.placement, values: BOOT.values || {} } : undefined, // WS-50: layer 6
      url: Object.fromEntries(params), host: { drafts: RUNTIME.allowDrafts, map: mapId, resort: RESORT_ID },
      ...(back && { visitor: back.layers.visitor, editor: back.layers.editor, loaded: kept?.id === preset.id }),
    },
  });
  const V = rt.api.get();
  // Resort-specific settings act as data: the package patch of the resolved value, merged like a season patch
  // (contract section 2). Applied before anything is built, so in v1 an `x.` setting changes with a reload.
  if (unpatched && registry.settings.some((e) => e.effects)) {
    for (const e of registry.settings) {
      const fx = e.effects?.[String(V[e.key])];
      if (fx?.resort) seasonMerge(resort, structuredClone(fx.resort));
      if (fx?.content) seasonMerge(content, structuredClone(fx.content));
    }
    unpatched = { resort: structuredClone(resort), content: structuredClone(content) };
  }
  // The package's own reading stays the package layer (resort.season, the ui.seasons entries marked ready); what a
  // layer above it says (the preset, ?season through the season switch, the calendar) comes from the runtime.
  const set = (key) => (['default', 'package', 'season'].includes(rt.source(key)) ? undefined : V[key]);
  applySeason(resort, content, set('season.current')); // WS-35
  const seasons = seasonConfigs(resort, content, unpatched, set('season.offered') ?? (Array.isArray(resort.ui?.seasons) ? resort.ui.seasons : []).filter((x) => x.ready).map((x) => x.id)); // WS-41: one config per season the page can show
  // ---- WS-51 hidden places (begin): `places.hidden` and `categories.hidden` of the preset (or of the editor's unsaved
  // values). The content every part of the page reads is a copy without them, per season, so a hidden place is in no
  // list, has no label and no card; main.js builds the scene without its building (`hidden.has`). The whole content
  // stays in `contentAll` for the editor's place list. Nothing hidden (always, without a preset) = the same objects.
  const contentAll = Object.fromEntries(Object.entries(seasons).map(([s, v]) => [s, v.content]));
  // WS-55: `let` and `setHidden`: in the editor's frame (liveHide) the scene is built with every place and what is
  // hidden changes live, so the content is laid again from `contentAll` for the new lists
  let hidden = null, removed = [];
  const setHidden = (places, categories) => {
    hidden = hiddenPlaces([...(Array.isArray(places) ? places : []), ...removed], categories, resort, Object.values(contentAll));
    for (const s of Object.keys(seasons)) seasons[s] = { ...seasons[s], content: withoutHidden(contentAll[s], hidden) };
    return hidden;
  };
  setHidden(V['places.hidden'], V['categories.hidden']);
  /** The places Fine-tune removed with their things (lazy/touches.js removedPlaces), hidden on every view as the
   *  preset's are: the content is laid again without them, before anything is built. `lay` false (the editor's frame,
   *  main.js LZ.setRemoved): only kept, the caller hides again with the values of now. */
  const setRemoved = (keys, lay = true) => {
    removed = keys.slice();
    if (!lay) return hidden;
    const h = setHidden(V['places.hidden'], V['categories.hidden']), now = seasons[resort.season];
    initContent(now.content, now.resort || resort, BASE, visible(bundle?.content));
    return h;
  };
  /** A content overlay (the bundle's, the editor's unsaved one) as the page may show it in a season. */
  const visible = (doc, season = resort.season) => withoutHidden(overlaySeason(doc, season), hidden, contentAll[season]);
  const view = seasons[resort.season].content;
  // the editor's unsaved texts and photos that a `reload` brought back (layer 9): shown from the first frame
  const over = Object.fromEntries(['content.texts', 'content.galleries'].map((k) => [k, rt.source(k) === 'editor' ? editorContent(V[k], bundle, BOOT.objects, location.origin, document.baseURI) : null]));
  // ---- WS-51 hidden places (end)
  rt.content(view); // the season's content (content.seasonal.<season> merged), for the content-backed settings
  const relang = () => setContentLang(rt.api.get('lang.current') || rt.api.get('lang.default'), rt.api.get('lang.default'), bundle?.langs);
  relang();
  rt.api.register('lang.current', relang); rt.api.register('lang.default', relang);
  initContent(view, resort, BASE, back ? [bundle?.content, over['content.texts'], over['content.galleries']].map((o) => visible(o)) : visible(bundle?.content));
  applyBrand(resort.brand);
  // brand values that a layer above the package sets (the bundle, the editor): now and live; VIS-208: the client's own
  // two colours (BOOT.colors) stand over every one of them
  for (const e of registry.settings) if (e.key.startsWith('brand.') && !(BOOT.colors && OWN_COLOR.has(e.key))) {
    if (!['default', 'package', 'season'].includes(rt.source(e.key))) brandValue(e.key, V[e.key]);
    rt.api.register(e.key, (v) => brandValue(e.key, v));
  }
  ownColors(BOOT.colors);
  // ---- WS-49 platform boot (end)
  const keyOf = new Map(Object.entries(assignments.assign).map(([mtk, v]) => [Number(mtk), v.key]));
  return { resort, content: view, assignments, scene, keyOf, draft, seasons, rt, bundle,
    hidden, contentAll, visible, over, restored: back, // WS-51: what the preset hides, the whole content, the editor's overlays, what `reload` kept
    setHidden, setRemoved, liveHide: editorFramed(BOOT, globalThis) }; // WS-55: what is hidden, changed live in the editor's frame; setRemoved: Fine-tune's removed places
}

// ---- WS-35 seasons (begin): one season per page load, resolved before anything is built.
// Season = ?season= (local/preview hosts, or any host for a resort.json ui.seasons entry with "ready": true, the
// round-6 season switch) or resort.season, default winter. resort.seasonal.<season> and content.seasonal.<season> are patches layered over
// the package like a preview (objects deep-merge; arrays of {id|key} items merge item by item, `_drop: true`
// removes one; other arrays replace). Features declare where they exist with `seasons: [...]` (main.js drops them
// outside); content places with `seasons` get an off-season notice on their card and leave the tour.
// Not in ?edit: the editor exports resort.json as a whole, so it must see the file as written.
export const SEASON_NAMES = ['winter', 'spring', 'summer', 'autumn'];
// WS-49: the season asked for is the resolved `season.current` (the preset, `?season` through the season switch, the
// calendar); the guard is unchanged: an unfinished season opens on draft hosts only.
const readySeason = (resort, id) => (Array.isArray(resort.ui?.seasons) ? resort.ui.seasons : []).some((x) => x.id === id && x.ready);
function applySeason(resort, content, asked) {
  const season = [asked && (RUNTIME.allowDrafts || readySeason(resort, asked) || asked === resort.season) ? asked : '', resort.season, 'winter'].find((s) => SEASON_NAMES.includes(s));
  resort.season = season;
  if (params.has('edit')) return;
  if (resort.seasonal?.[season]) seasonMerge(resort, resort.seasonal[season]);
  if (content.seasonal?.[season]) seasonMerge(content, content.seasonal[season]);
}
const idOf = (v) => (v && typeof v === 'object' ? v.id ?? v.key ?? null : null);
// (security review FE-2, 2026-10-14: a key that reaches an object's prototype is never walked into or written)
const UNSAFE_KEY = new Set(['__proto__', 'constructor', 'prototype']);
function seasonMerge(target, patch) {
  for (const [k, v] of Object.entries(patch)) {
    if (UNSAFE_KEY.has(k)) continue;
    const cur = target[k];
    if (Array.isArray(v) && Array.isArray(cur) && v.length && v.every((x) => idOf(x) !== null) && cur.some((x) => idOf(x) !== null)) {
      for (const item of v) {
        const i = cur.findIndex((x) => idOf(x) === idOf(item));
        if (item._drop) { if (i >= 0) cur.splice(i, 1); continue; }
        if (i >= 0) seasonMerge(cur[i], item); else cur.push(item);
      }
    } else if (v && typeof v === 'object' && !Array.isArray(v) && cur && typeof cur === 'object' && !Array.isArray(cur)) seasonMerge(cur, v);
    else target[k] = v;
  }
}
// ---- WS-41 live seasons (begin): the season is a runtime state, not a page. Every season the page can show (the
// ui.seasons entries marked ready, plus the one it opens in) gets its own config object, resolved once at load: the
// package (previews + draft) with that season's resort/content patch. The opening season's object is the `resort`
// the scene is built from (so ?season= deep links and the single-season live view are unchanged); main.js switches
// between them without a reload (setSeason). One season available (or ?edit) = no extra objects.
// WS-49: the seasons built are `season.offered` (without a preset: the ui.seasons entries marked ready, as before),
// so a preset pays only for the seasons it uses.
function seasonConfigs(resort, content, unpatched, offered) {
  const out = { [resort.season]: { resort, content } };
  if (!unpatched) return out;
  for (const s of Array.isArray(offered) ? offered : []) {
    if (out[s] || !SEASON_NAMES.includes(s) || !(RUNTIME.allowDrafts || readySeason(resort, s))) continue;
    const r = structuredClone(unpatched.resort), c = structuredClone(unpatched.content);
    r.season = s;
    if (r.seasonal?.[s]) seasonMerge(r, r.seasonal[s]);
    if (c.seasonal?.[s]) seasonMerge(c, c.seasonal[s]);
    out[s] = { resort: r, content: c };
  }
  return out;
}
// ---- WS-41 live seasons (end)
// ---- WS-35 seasons (end)

function deepMerge(target, patch) {
  for (const [k, v] of Object.entries(patch)) {
    if (UNSAFE_KEY.has(k)) continue;
    if (v && typeof v === 'object' && !Array.isArray(v) && target[k] && typeof target[k] === 'object' && !Array.isArray(target[k])) deepMerge(target[k], v);
    else target[k] = v;
  }
}

// WS-49: one brand value, live (colours only as hex, names as text; contract section 12)
// VIS-208: the client's own colours (the portal's Map style page): the buttons (--gold) and the panels (--ink) of Night
// Glass, and of the dark styles their accent, button and surface (css/styles/*.css: "a client override, the same
// properties inline on <body>"). Everywhere the words on a button are the panel colour and the words on a panel are
// light; the API checks both read (apps/portal/worker/colors.ts).
const OWN_COLOR = new Set(['brand.colors.accent', 'brand.colors.ink']);
function ownColors(c) {
  if (!c) return;
  const root = document.documentElement.style;
  root.setProperty('--gold', c.accent); root.setProperty('--gold-deep', c.accent); root.setProperty('--ink', c.ink);
  // Night Glass's glass panels are its own near-black (css/style.css --glass, --glass-strong): tinted by the panel colour
  root.setProperty('--glass', `color-mix(in srgb, ${c.ink} 66%, transparent)`); root.setProperty('--glass-strong', `color-mix(in srgb, ${c.ink} 86%, transparent)`);
  const body = document.body.style;
  body.setProperty('--st-accent', c.accent); body.setProperty('--st-button', c.accent); body.setProperty('--st-surface', c.ink);
}
const BRAND_VAR = { accent: '--gold', ink: '--ink', cabin: '--c-cabin', suite: '--c-suite', tower: '--c-tower', towerSuite: '--c-towerSuite' };
const fontName = (v) => String(v).replace(/[^\w .-]/g, '');
function brandValue(key, v) {
  const root = document.documentElement.style, k = key.split('.').pop();
  if (key.startsWith('brand.colors.')) { if (BRAND_VAR[k] && /^#[0-9a-f]{3,8}$/i.test(v)) root.setProperty(BRAND_VAR[k], v); }
  else if (key === 'brand.unitBadge') { if (Number.isFinite(v)) root.setProperty('--unit-badge', `${v}px`); }
  else if (key === 'brand.fonts.display' && v) root.setProperty('--font-display', `'${fontName(v)}', 'Times New Roman', serif`);
  else if (key === 'brand.fonts.ui' && v) root.setProperty('--font-ui', `'${fontName(v)}', system-ui, sans-serif`);
  else if (key === 'brand.logoPlate') document.documentElement.dataset.logo = v === false ? 'bare' : 'plate';
  else if (key === 'brand.name' && v) document.title = `${v} · Resort Map`;
}

function applyBrand(brand = {}) {
  const root = document.documentElement.style;
  const c = brand.colors || {};
  if (c.accent) root.setProperty('--gold', c.accent);
  if (c.ink) root.setProperty('--ink', c.ink);
  for (const k of ['cabin', 'suite', 'tower', 'towerSuite']) if (c[k]) root.setProperty(`--c-${k}`, c[k]);
  if (Number.isFinite(brand.unitBadge)) root.setProperty('--unit-badge', `${brand.unitBadge}px`); // WS-15: unit number badge size (default 21)
  // WS-23: brand fonts are self-hosted; only a same-origin stylesheet is loaded (CSP style-src 'self', no Google Fonts).
  const fontCss = brand.fonts?.css ? new URL(brand.fonts.css, BOOT.assets ? new URL(BOOT.assets, location.origin) : document.baseURI) : null; // WS-50: under the asset root when the host names one (VIS-195: else the page's <base>)
  if (fontCss && fontCss.origin !== location.origin) console.warn(`brand.fonts.css must be same-origin, ignored: ${fontCss.href}`);
  // VIS-239: the page's own link may name the stylesheet's hashed copy (scripts/fingerprint.mjs: fonts.h-<hash>.css), the same file
  else if (fontCss && ![...document.querySelectorAll('link[rel="stylesheet"]')].some((l) => l.href.replace(/\.h-[0-9a-f]{8,10}(?=\.css$)/, '') === fontCss.href)) {
    const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = fontCss.href; document.head.appendChild(l);
  }
  if (brand.fonts?.display) root.setProperty('--font-display', `'${brand.fonts.display}', 'Times New Roman', serif`);
  if (brand.fonts?.ui) root.setProperty('--font-ui', `'${brand.fonts.ui}', system-ui, sans-serif`);
  if (brand.name) document.title = `${brand.name} · Resort Map`;
  const logo = document.querySelector('#loader img');
  if (logo && brand.logo) logo.src = BASE + brand.logo;
  else logo?.closest?.('#loader')?.classList.add('no-logo'); // no emblem: the loader gives up the slot it kept for one
  // Round 6: brand.logoPlate false = the logo is drawn for dark backgrounds (no white disc behind it);
  // brand.showName false = the header shows the logo alone (ui.js).
  document.documentElement.dataset.logo = brand.logoPlate === false ? 'bare' : 'plate';
  const h = document.querySelector('#loader h2');
  if (h && brand.name) h.textContent = brand.name;
}
